import { Adjust } from "@awesome-cordova-plugins/adjust";
import { AppleProviderResponse, GoogleLoginResponseOnline } from "@capgo/capacitor-social-login";
import { Typography, Box, makeStyles } from "@material-ui/core";
import CheckCircleOutlineSharpIcon from "@material-ui/icons/CheckCircleOutlineSharp";
import * as React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";

import {
  JobCategory,
  JobIndustry,
  MoneyAmount,
  Period,
  RegistrationMethod,
  UserRegistrationsApple,
  UserRegistrationsGoogle,
  UserUpdate,
} from "@/api/generated";
import { EXISTING_AUTH_CODE_MESSAGE, sendPhoneNumberAuthCode } from "@/api/sendPhoneNumberAuthCode";
import { CustomButton } from "@/components/Button";
import { CodeInput, CodeInputCell } from "@/components/CodeInput";
import { FormErrorText } from "@/components/FormErrorText";
import { Loading } from "@/components/Loading";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { TextLink } from "@/components/TextLink";
import { DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA } from "@/constants/account";
import { SMS_AUTH_CODE_LENGTH } from "@/constants/smsAuthCode";
import { useResendCooldown } from "@/hooks/useResendCooldown";
import { useSmsCodeAutofill } from "@/hooks/useSmsCodeAutofill";
import { RegistrationFlowLayout as EntranceTemplate } from "@/layouts/RegistrationFlowLayout";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { isNativeApp } from "@/utilities/capacitorLogic";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { parseFormattedNumber } from "@/utilities/format";
import { getStorageAccountRegistrationUserId, setStorageAccountRegistrationState } from "@/utilities/storage";
import { AccountRegisterFormData } from "@/validations/account";
import { annualIncomeSchema, getAnnualIncomeError } from "@/validations/annualIncome";
import { RegisterFullFormData } from "@/validations/register";
import {
  RegistrationMethodState,
  SessionState,
  UserRegistrationsAppleFormState,
  UserRegistrationsAccoutFormState,
  UserRegistrationsFormState,
  UserRegistrationsGoogleFormState,
} from "@/view_models/atoms";

import { PostRegisterRequestBody } from "../types/types";

enum SmsVerificationStatus {
  INITIAL = 0, // 初期状態
  VERIFYING = 1, // 認証中
  VERIFIED = 2, // 認証成功
}

// 6 桁のコードがどこから来たか。input は手入力・OS の候補入力・貼り付け、autofill は useSmsCodeAutofill 経由
type CodeInputSource = "input" | "autofill";

const useStyles = makeStyles(() => {
  return {
    boxContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%", // 幅を100%に設定
    },
    errorMessage: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(1),
      fontSize: theme.typography.caption.fontSize,
    },
    // 再送できるようになるまでの間のリンク（押せないことが分かるよう薄くする）
    resendDisabled: {
      color: theme.palette.text.disabled,
    },
    resendNotice: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1),
      fontSize: theme.typography.caption.fontSize,
    },
  };
});

interface RegisterSmsVerificationCodePageProps {
  onBack: () => void;
  // 再送できるようになるまでの秒数。電話番号画面で 429（送信済み）だった場合に API の残り秒数を引き継ぐ
  initialCooldownSeconds?: number;
  initialCodeAvailable?: boolean;
}

// Adjust Attribution から network を取得（取得できた場合のみ返却）
// 検証環境ではAdjustを初期化しないため、ネイティブアプリにて適切に値が返らない。
// またフェイルセーフとしてタイムアウトを設けてAPI呼び出しをブロックしないようにする
const getAdjustNetwork = async (): Promise<string | undefined> => {
  if (!isNativeApp) return undefined;

  const fetch = async () => {
    try {
      const hasGetter = typeof (Adjust as any)?.getAttribution === "function";
      if (!hasGetter) return undefined;
      const attribution = await Adjust.getAttribution();
      const v = attribution?.network?.trim();
      return v || undefined;
    } catch (_) {
      return undefined;
    }
  };

  const timeoutMs = 800;
  const timeout = new Promise<string | undefined>((resolve) => setTimeout(() => resolve(undefined), timeoutMs));
  return Promise.race([fetch(), timeout]);
};

// APIの呼び出し
const postRegistrations = async (data: PostRegisterRequestBody) => {
  const fp = PropallyApiFactory();
  try {
    const res = await fp.v1RegistrationsPost(data, { withCredentials: true });
    return { success: true, error: "", userId: (res.data as any)?.id as number };
  } catch (e) {
    if (e.response?.status === 422) {
      switch (e.response?.data.code) {
        case "REGISTERED":
          return {
            success: false,
            error: "入力されたメールアドレスは既に登録済みです",
          };
        case "INVALID_PHONE_NUMBER_AUTH_CODE":
          return {
            success: false,
            error: "電話番号認証コードが不正です",
          };
        default:
          return {
            success: false,
            error: "入力内容が不正です",
          };
      }
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

const postLogin = async (data: { email: string; password: string }) => {
  const fp = PropallyApiFactory();
  try {
    const adjust_network = await getAdjustNetwork();
    await fp.v1LoginPost(
      {
        ...data,
        ...(adjust_network ? { adjust_network } : {}),
      },
      { withCredentials: true }
    );
    return { success: true, error: "" };
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        success: false,
        error: "メールアドレスまたはパスワードが不正です",
      };
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

// Googleアカウント用ログインAPIの呼び出し
const postRegistrationsWithGoogle = async (data: UserRegistrationsGoogle) => {
  const fp = PropallyApiFactory();
  try {
    const res = await fp.v1RegistrationsGooglePost(data, { withCredentials: true });
    return { success: true, error: "", userId: (res.data as any)?.id as number };
  } catch (e) {
    if (e.response?.status === 422) {
      switch (e.response?.data.code) {
        case "REGISTERED":
          return {
            success: false,
            error: "入力されたメールアドレスは既に登録済みです",
          };
        case "INVALID_PHONE_NUMBER_AUTH_CODE":
          return {
            success: false,
            error: "電話番号認証コードが不正です",
          };
        case "REGISTERED_WITH_EXTERNAL_AUTH":
          return {
            success: false,
            error: "このメールアドレスは外部サービスで登録されています",
          };
        default:
          return {
            success: false,
            error: "入力内容が不正です",
          };
      }
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

const postLoginWithGoogle = async (data: { social_login_response: GoogleLoginResponseOnline }) => {
  const fp = PropallyApiFactory();
  try {
    const adjust_network = await getAdjustNetwork();
    await fp.v1LoginGooglePost(
      {
        ...data,
        ...(adjust_network ? { adjust_network } : {}),
      },
      { withCredentials: true }
    );
    return { success: true, error: "" };
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        success: false,
        error: "アカウントが存在しません。先にアカウントを登録してください。",
      };
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

// Appleアカウント用ログインAPIの呼び出し
const postRegistrationsWithApple = async (data: UserRegistrationsApple) => {
  const fp = PropallyApiFactory();
  try {
    const res = await fp.v1RegistrationsApplePost(data, { withCredentials: true });
    return { success: true, error: "", userId: (res.data as any)?.id as number };
  } catch (e) {
    if (e.response?.status === 422) {
      switch (e.response?.data.code) {
        case "REGISTERED":
          return {
            success: false,
            error: "入力されたメールアドレスは既に登録済みです",
          };
        case "INVALID_PHONE_NUMBER_AUTH_CODE":
          return {
            success: false,
            error: "電話番号認証コードが不正です",
          };
        case "REGISTERED_WITH_EXTERNAL_AUTH":
          return {
            success: false,
            error: "このメールアドレスは外部サービスで登録されています",
          };
        default:
          return {
            success: false,
            error: "入力内容が不正です",
          };
      }
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

const postLoginWithApple = async (data: { social_login_response: AppleProviderResponse }) => {
  const fp = PropallyApiFactory();
  try {
    const adjust_network = await getAdjustNetwork();
    await fp.v1LoginApplePost(
      {
        ...data,
        ...(adjust_network ? { adjust_network } : {}),
      },
      { withCredentials: true }
    );
    return { success: true, error: "" };
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        success: false,
        error: "アカウントが存在しません。先にアカウントを登録してください。",
      };
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

const buildUserUpdateFromAccountFormData = (data: AccountRegisterFormData): UserUpdate | null => {
  const month = data.birth_month ? String(data.birth_month).padStart(2, "0") : "";
  const birthday = data.birth_year && data.birth_month ? `${data.birth_year}-${month}-01` : "";

  const hasHoldingPropertyCount = data.holding_property_count !== "";
  const realEstateLoanAmountValue = data.real_estate_loan_amount
    ? parseFormattedNumber(data.real_estate_loan_amount)
    : 0;

  const fields: Array<[keyof UserUpdate, unknown]> = [
    ["birthday", birthday],
    ["job_category", data.job_category ? (data.job_category as JobCategory) : ""],
    ["job_industry", data.job_industry ? (data.job_industry as JobIndustry) : ""],
    ["job_continuous_period", data.job_continuous_period ? (data.job_continuous_period as Period) : ""],
    [
      "annual_income",
      annualIncomeSchema.safeParse(data.annual_income).success ? parseFormattedNumber(data.annual_income) : "",
    ],
    ["has_investment_experience", typeof data.has_investment_experience === "boolean" ? data.has_investment_experience : ""],
    ["financial_asset", data.financial_asset ? (data.financial_asset as MoneyAmount) : ""],
    ["holding_property_count", data.holding_property_count !== "" ? Number(data.holding_property_count) : ""],
    ["real_estate_loan_amount", hasHoldingPropertyCount ? realEstateLoanAmountValue : ""],
  ];

  const payload = fields.reduce<UserUpdate>((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      (acc as any)[key] = value;
    }
    return acc;
  }, {});

  return Object.keys(payload).length ? payload : null;
};

const RegisterSmsVerificationCodePage: React.FC<RegisterSmsVerificationCodePageProps> = ({
  onBack,
  initialCooldownSeconds,
  initialCodeAvailable = false,
}) => {
  const classes = useStyles();
  // SMS認証の状態
  const [isSmsVerified, setIsSmsVerified] = React.useState<SmsVerificationStatus>(SmsVerificationStatus.INITIAL);

  const [error, setError] = React.useState<string>("");
  const { control, setValue, getValues, trigger } = useFormContext();
  const inputsRef = React.useRef([]);
  const formData = useRecoilValue(UserRegistrationsFormState);
  const resetFormData = useResetRecoilState(UserRegistrationsFormState);
  const history = useHistory();
  const setSession = useSetRecoilState(SessionState);

  const googleFormData = useRecoilValue(UserRegistrationsGoogleFormState);
  const resetGoogleFormData = useResetRecoilState(UserRegistrationsGoogleFormState);
  const appleFormData = useRecoilValue(UserRegistrationsAppleFormState);
  const resetAppleFormData = useResetRecoilState(UserRegistrationsAppleFormState);
  const [accountFormData, setAccountFormData] = useRecoilState(UserRegistrationsAccoutFormState);
  const registrationMethod = useRecoilValue(RegistrationMethodState);
  const resetRegistrationMethod = useResetRecoilState(RegistrationMethodState);

  // 認証コードの再送。以前は「戻る」と同じ onBack で電話番号画面に戻していたが、画面を離れずに同じ番号へ送り直す
  const {
    remaining: resendRemaining,
    canResend,
    restart: restartResendCooldown,
  } = useResendCooldown(initialCooldownSeconds);
  const [isResending, setIsResending] = React.useState(false);
  const [resendNotice, setResendNotice] = React.useState<{ type: "info" | "error"; text: string } | null>(
    initialCodeAvailable ? { type: "info", text: EXISTING_AUTH_CODE_MESSAGE } : null
  );
  // SMS からの自動入力の待受をやり直した回数。再送で新しいコードを待つときに進める
  const [autofillRestartKey, setAutofillRestartKey] = React.useState(0);
  // 登録 API の送信中フラグ。VERIFYING は onSubmit* 内の検証後にしか立たないため、
  // その間に手入力・貼り付け・SMS からの自動入力が重なっても二重に送らないよう ref で守る
  const submittingRef = React.useRef(false);
  // 直前に送った認証コード。再送後に遅れて届いた古い SMS などで自動入力に同じコードが二度届いても、
  // API の試行回数を消費しないよう送らない。ユーザー操作（手入力・候補入力・貼り付け）による再試行には
  // 効かせず、再送で解除する
  const attemptedCodeRef = React.useRef("");
  // 再送の応答待ちや完了表示中に画面を離れた場合の更新・遷移を防ぐ。
  const isMountedRef = React.useRef(true);
  const completionTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (completionTimerRef.current !== null) clearTimeout(completionTimerRef.current);
    };
  }, []);

  const completeRegistration = React.useCallback(
    (delay: number) => {
      // 途中で登録方法を切り替えた場合も、資格情報をすべて初期値へ戻す。診断の下書きは保持する。
      resetFormData();
      resetGoogleFormData();
      resetAppleFormData();
      resetRegistrationMethod();
      if (!isMountedRef.current) return;
      setIsSmsVerified(SmsVerificationStatus.VERIFIED);
      if (completionTimerRef.current !== null) clearTimeout(completionTimerRef.current);
      completionTimerRef.current = setTimeout(() => {
        completionTimerRef.current = null;
        history.replace("/first_account");
      }, delay);
    },
    [resetFormData, resetGoogleFormData, resetAppleFormData, resetRegistrationMethod, history]
  );

  // 認証コードの送信先。電話番号画面で登録方法ごとの Recoil state に保存している
  const phoneNumber = React.useMemo(() => {
    switch (registrationMethod) {
      case RegistrationMethod.Google:
        return googleFormData?.phone_number;
      case RegistrationMethod.Apple:
        return appleFormData?.phone_number;
      default:
        return formData?.phone_number;
    }
  }, [registrationMethod, formData, googleFormData, appleFormData]);

  const clearCodeInputs = React.useCallback(() => {
    // 新しいコードを待つので、前回送ったコードの抑止も解く
    attemptedCodeRef.current = "";
    Array.from({ length: 6 }).forEach((_, index) => {
      setValue(`code${index}`, "");
      if (inputsRef.current[index]) {
        inputsRef.current[index].value = "";
      }
    });
    inputsRef.current[0]?.focus();
  }, [setValue]);

  const handleResend = React.useCallback(async () => {
    if (!canResend || isResending) return;
    if (!phoneNumber) {
      // 送信先が無い（state が消えている）場合は電話番号画面からやり直す
      onBack();
      return;
    }
    setIsResending(true);
    setResendNotice(null);
    const result = await sendPhoneNumberAuthCode(phoneNumber);
    if (!isMountedRef.current) return;
    if (result.success) {
      // 古いコードは無効になるので入力欄と前回の認証エラーを消す
      clearCodeInputs();
      setError("");
      setResendNotice({ type: result.kind, text: "認証コードを再送しました。" });
      restartResendCooldown(result.retryAfter);
      // 新しいコードの SMS を待ち直す
      setAutofillRestartKey((key) => key + 1);
    } else {
      setResendNotice({ type: result.kind, text: result.error });
      // 429 なら API が返した残り秒数でカウントダウンを合わせる（画面側の時計とのずれを吸収）
      if (result.retryAfter !== undefined) restartResendCooldown(result.retryAfter);
    }
    setIsResending(false);
  }, [canResend, isResending, phoneNumber, onBack, clearCodeInputs, restartResendCooldown]);

  const bindPreDiagnosisToUser = React.useCallback((userId?: number) => {
    const owner = getStorageAccountRegistrationUserId();
    const source = owner === null || owner === userId ? accountFormData : DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA;
    const draft = {
      ...source,
      annual_income: annualIncomeSchema.safeParse(source.annual_income).success ? source.annual_income : "",
    };
    setStorageAccountRegistrationState(draft, userId);
    setAccountFormData(draft);
    return draft;
  }, [accountFormData, setAccountFormData]);

  const savePreDiagnosisToUser = React.useCallback(async (draft: AccountRegisterFormData) => {
    const payload = buildUserUpdateFromAccountFormData(draft);
    if (!payload) return;
    const fp = PropallyApiFactory();
    try {
      await fp.v1UserPatch(payload, { withCredentials: true });
    } catch (error) {
      // サーバーが年収だけを拒否した場合も、それ以外の診断回答は失わない。
      if (getAnnualIncomeError(error) && payload.annual_income != null) {
        const remaining = { ...payload };
        delete remaining.annual_income;
        try {
          if (Object.keys(remaining).length) await fp.v1UserPatch(remaining, { withCredentials: true });
        } catch (_) {
          // 保存できなかった回答は下書きから本登録で復元する。
        }
      }
      // 事前入力の保存失敗は致命的ではないため握りつぶす
    }
  }, []);

  // UIのレンダリング処理
  const renderContent = () => {
    switch (isSmsVerified) {
      case SmsVerificationStatus.VERIFIED:
        return <CheckCircleOutlineSharpIcon style={{ color: "white", fontSize: "3rem" }} />;
      case SmsVerificationStatus.VERIFYING:
        return <Loading />;
      default:
        return null;
    }
  };

  const onSubmit = React.useCallback(
    async (data: RegisterFullFormData) => {
      setValue("phone_number_auth_code", data.phone_number_auth_code);
      const validate_result = await trigger("phone_number_auth_code");
      if (!validate_result) return;

      // 認証中画面の表示
      setIsSmsVerified(SmsVerificationStatus.VERIFYING);

      const sendValues: PostRegisterRequestBody = {
        ...data,
        phone_number: data.phone_number,
        phone_number_auth_code: data.phone_number_auth_code,
        email: data.email,
        password: data.password,
        // 確認用パスワードを入力させないよう仮変更
        password_confirmation: data.password,
        // 「不動産会社からのオファー希望」は現状、非表示項目となるので、true(希望する)で固定値を送信する
        request_the_proposal: true,
        // TODO 以下方針が確定した場合は既存のAPI側も修正する方が望ましい
        // 名前については詳細登録時に上書きするため、仮登録では固定値を送信する
        family_name: "仮苗字",
        family_name_kana: "ミョウジ",
        first_name: "仮名前",
        first_name_kana: "ナマエ",
      };

      const result = await postRegistrations(sendValues);
      if (result.success) {
        const diagnosisDraft = bindPreDiagnosisToUser(result.userId);
        // Adjust/Firebase へユーザーIDを付与して送信
        sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationCompleted, {
          user_id: result.userId,
        });
        // 会員登録完了したら、ログインを行う
        const loginResult = await postLogin({ email: data.email, password: data.password });
        setSession({
          logged_in: loginResult.success,
        });
        if (loginResult.success) {
          await savePreDiagnosisToUser(diagnosisDraft);
        }

        // sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationCompleted);

        completeRegistration(750);
      } else {
        // 登録に失敗した場合もAdjustイベントを発火
        sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationFailed);
        setError(result.error);
        setIsSmsVerified(SmsVerificationStatus.INITIAL);
      }
    },
    [trigger, completeRegistration, bindPreDiagnosisToUser, savePreDiagnosisToUser, setSession, setValue]
  );

  const onSubmitForGoogle = React.useCallback(
    async (data: UserRegistrationsGoogle) => {
      setValue("phone_number_auth_code", data.phone_number_auth_code);
      const validate_result = await trigger("phone_number_auth_code");
      if (!validate_result) return;

      // 認証中画面の表示
      setIsSmsVerified(SmsVerificationStatus.VERIFYING);

      const sendValues: UserRegistrationsGoogle = {
        ...data,
      };

      const result = await postRegistrationsWithGoogle(sendValues);
      if (result.success) {
        const diagnosisDraft = bindPreDiagnosisToUser(result.userId);
        // Adjust/Firebase へユーザーIDを付与して送信
        sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationGoogleCompleted, {
          user_id: result.userId,
        });
        // 会員登録完了したら、ログインを行う
        const loginResult = await postLoginWithGoogle({
          social_login_response: sendValues.social_login_response as GoogleLoginResponseOnline,
        });

        setSession({
          logged_in: loginResult.success,
        });
        if (loginResult.success) {
          await savePreDiagnosisToUser(diagnosisDraft);
        }
        completeRegistration(3000);
      } else {
        // 登録に失敗した場合もAdjustイベントを発火
        sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationFailed);
        setError(result.error);
        setIsSmsVerified(SmsVerificationStatus.INITIAL);
      }
    },
    [trigger, completeRegistration, bindPreDiagnosisToUser, savePreDiagnosisToUser, setSession, setValue]
  );

  const onSubmitForApple = React.useCallback(
    async (data: UserRegistrationsApple) => {
      setValue("phone_number_auth_code", data.phone_number_auth_code);
      const validate_result = await trigger("phone_number_auth_code");
      if (!validate_result) return;

      // 認証中画面の表示
      setIsSmsVerified(SmsVerificationStatus.VERIFYING);

      const sendValues: UserRegistrationsApple = {
        ...data,
      };

      const result = await postRegistrationsWithApple(sendValues);
      if (result.success) {
        const diagnosisDraft = bindPreDiagnosisToUser(result.userId);
        // Adjust/Firebase へユーザーIDを付与して送信
        sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationAppleCompleted, {
          user_id: result.userId,
        });
        // 会員登録完了したら、ログインを行う
        const loginResult = await postLoginWithApple({
          social_login_response: sendValues.social_login_response as AppleProviderResponse,
        });

        setSession({
          logged_in: loginResult.success,
        });
        if (loginResult.success) {
          await savePreDiagnosisToUser(diagnosisDraft);
        }
        completeRegistration(3000);
      } else {
        // 登録に失敗した場合もAdjustイベントを発火
        sendEventToAppMeasurementTool(AppEventTokenEnum.UserRegistrationFailed);
        setError(result.error);
        setIsSmsVerified(SmsVerificationStatus.INITIAL);
      }
    },
    [trigger, completeRegistration, bindPreDiagnosisToUser, savePreDiagnosisToUser, setSession, setValue]
  );

  // 6 桁そろったコードを登録方法ごとの送信処理へ渡す。手入力・貼り付け・SMS からの自動入力で共通。
  // 送信中は送らない。自動入力で直前と同じコードが二度届いたときも送らないが、ユーザー操作による再試行は止めない
  const submitCode = React.useCallback(
    async (code: string, source: CodeInputSource) => {
      if (submittingRef.current) return;
      if (source === "autofill" && attemptedCodeRef.current === code) return;
      submittingRef.current = true;
      attemptedCodeRef.current = code;
      try {
        const phone_number_auth_code = parseInt(code);
        if (registrationMethod === RegistrationMethod.Google) {
          // Google認証を用いたアカウント登録
          await onSubmitForGoogle({ ...googleFormData, phone_number_auth_code });
        } else if (registrationMethod === RegistrationMethod.Apple) {
          // Apple認証を用いたアカウント登録
          await onSubmitForApple({ ...appleFormData, phone_number_auth_code });
        } else {
          // パスワードを用いたアカウント登録
          await onSubmit({ ...formData, phone_number_auth_code });
        }
      } finally {
        submittingRef.current = false;
      }
    },
    [registrationMethod, formData, googleFormData, appleFormData, onSubmit, onSubmitForGoogle, onSubmitForApple]
  );

  // 複数桁をまとめて入力欄へ反映する（OS の候補入力・貼り付け・SMS からの自動入力）。6 桁そろえば送信する
  const fillCode = React.useCallback(
    (text: string, source: CodeInputSource = "input") => {
      const digits = text.replace(/\D/g, "").slice(0, SMS_AUTH_CODE_LENGTH).split("");
      digits.forEach((digit, index) => {
        setValue(`code${index}`, digit);
        if (inputsRef.current[index]) {
          inputsRef.current[index].value = digit;
        }
      });
      // 必要なら最後のinputにfocus
      if (inputsRef.current[digits.length - 1]) {
        setTimeout(() => {
          inputsRef.current[digits.length - 1]?.focus();
        }, 0);
      }
      if (digits.length === SMS_AUTH_CODE_LENGTH) submitCode(digits.join(""), source);
    },
    [setValue, submitCode]
  );

  // 端末に届いた SMS からの自動入力（Trello #157）。送信中・完了後と、再送の応答待ち（古い SMS を拾わないため）は
  // 待受を止め、再送で新しいコードを待ち直す
  useSmsCodeAutofill({
    enabled: isSmsVerified === SmsVerificationStatus.INITIAL && !isResending,
    onCode: (code) => fillCode(code, "autofill"),
    restartKey: autofillRestartKey,
  });

  const handleChange = React.useCallback(
    (e, index) => {
      const { value } = e.target;
      // 1. 複数桁入力（自動入力やコピペなど）対応
      if (value.length > 1) {
        fillCode(value);
        return;
      }

      // 2. 手動の1文字入力（削除も含む）
      if (value.match(/^[0-9]$/)) {
        setValue(`code${index}`, value);
        if (index < 5) {
          inputsRef.current[index + 1]?.focus();
        } else {
          // 6桁揃ったら自動送信
          const digits = Array.from({ length: 6 }, (_, n) => getValues(`code${n}`) || "");
          if (digits.some((digit) => !/^[0-9]$/.test(digit))) return;
          submitCode(digits.join(""), "input");
        }
      } else if (value === "") {
        setValue(`code${index}`, "");
      }
    },
    [getValues, setValue, fillCode, submitCode]
  );

  const handleKeyDown = React.useCallback(
    (e, index) => {
      if (e.key === "Backspace" && !getValues(`code${index}`) && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    },
    [getValues]
  );

  // クリップボードからの読み込み機能
  const handlePaste = async (e) => {
    e.preventDefault();
    let text = "";
    if (window.navigator.clipboard && window.navigator.clipboard.readText) {
      try {
        text = await window.navigator.clipboard.readText();
      } catch (err) {
        // 無視して次の手段へ
      }
    }
    // clipboardData fallback
    if (!text && e.clipboardData) {
      text = e.clipboardData.getData("Text") || "";
    }
    fillCode(text);
  };

  return (
    <EntranceTemplate
      phaseTags={<RegistrationPhases completed />}
      title={"認証コードを\n入力してください"}
      description={phoneNumber ? `${phoneNumber.slice(0, 3)}****${phoneNumber.slice(-4)} 宛に送信しました` : undefined}
    >
      <Box>
        <Box className={classes.boxContainer}>
          <CodeInput>
            {Array.from({ length: 6 }).map((_, index) => (
              <Controller
                key={index}
                name={`code${index}`}
                control={control}
                defaultValue=""
                render={({ ref, ...field }) => (
                  <CodeInputCell
                    index={index}
                    error={!!error}
                    inputRef={(el) => {
                      inputsRef.current[index] = el;
                      ref.current = el;
                    }}
                    {...field}
                    onChange={(e) => handleChange(e, index)}
                    type="text" // "number"はiOS端末での入力時に挙動バグるため利用しない
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => handlePaste(e)}
                    variant="outlined"
                    inputProps={{
                      maxLength: index === 0 ? 10 : 1, // 最初のinputだけ10文字
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                      autoComplete: index === 0 ? "one-time-code" : undefined, // 1つ目だけ
                    }}
                  />
                )}
              />
            ))}
          </CodeInput>
        </Box>
        {error && <FormErrorText role="alert">{error}</FormErrorText>}
        <Box display="flex" flexDirection="column" alignItems="center" margin="40px auto" width="300px">
          {canResend && !isResending ? (
            <TextLink href="" arrow onClick={handleResend}>
              <Typography variant="body1">認証コードを再送する</Typography>
            </TextLink>
          ) : (
            <Typography variant="body1" className={classes.resendDisabled}>
              {isResending ? "認証コードを再送しています…" : `認証コードを再送する（あと${resendRemaining}秒）`}
            </Typography>
          )}
          <Typography variant="caption" role="status">
            {canResend && !isResending ? "認証コードを再送できます。" : ""}
          </Typography>
          {resendNotice && (
            <Typography
              variant="caption"
              role={resendNotice.type === "error" ? "alert" : "status"}
              className={resendNotice.type === "error" ? classes.errorMessage : classes.resendNotice}
            >
              {resendNotice.text}
            </Typography>
          )}
        </Box>
        <Box display="flex" mt={3} justifyContent="center">
          <CustomButton type="button" tone="text" onClick={onBack}>
            戻る
          </CustomButton>
        </Box>
      </Box>
      {isSmsVerified !== SmsVerificationStatus.INITIAL && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          bgcolor="rgba(0, 0, 0, 0.5)"
        >
          {renderContent()}
        </Box>
      )}
    </EntranceTemplate>
  );
};

export default RegisterSmsVerificationCodePage;
